import { useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Node as TiptapNode } from '@tiptap/core';
import { useEditor, EditorContent } from '@tiptap/react';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Underline } from '@tiptap/extension-underline';
import { Strike } from '@tiptap/extension-strike';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { UndoRedo } from '@tiptap/extensions';
import { TextStyle, Color, FontSize } from '@tiptap/extension-text-style';
import Sketch from '@uiw/react-color-sketch';
import { TAILWIND_PALETTE } from '../lib/colorPaletteConstants';

// ============================================
// Module-level constants
// ============================================

const Document = TiptapNode.create({
  name: 'doc',
  topNode: true,
  content: 'block+',
});

const TIPTAP_EXTENSIONS = [Document, Paragraph, Text, Bold, Italic, Underline, Strike, UndoRedo, TextStyle, Color, FontSize];

const FONT_SIZES = ['10px', '12px', '14px', '16px', '20px', '24px', '32px', '48px'];
const SKETCH_PRESET_COLORS = ['#000000', '#374151', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#2563eb', '#7c3aed', '#db2777', '#ffffff', ...TAILWIND_PALETTE];

// ============================================
// InlineRichEditor API
// ============================================

export interface InlineRichEditorProps {
  entityId: string;
  html?: string;
  text: string;
  onSave: (html: string, text: string) => void;
  onCancel: () => void;
  className?: string;
}

// ============================================
// BubbleMenu: floating toolbar for inline formatting
// ============================================

function BubbleButton({ label, isActive, onMouseDown }: { label: string; isActive: boolean; onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <button
      className={`px-2 py-1 text-xs font-semibold cursor-pointer ${isActive ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}
      onMouseDown={(e) => { e.preventDefault(); onMouseDown(e); }}
    >
      {label}
    </button>
  );
}

function InlineRichBubbleMenu({ editor, portalRef }: { editor: ReturnType<typeof useEditor>; portalRef: React.RefObject<HTMLDivElement | null> }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [showColors, setShowColors] = useState(false);
  const [showSizes, setShowSizes] = useState(false);

  useEffect(() => {
    if (!editor) return;

    const updatePos = () => {
      const { from, to } = editor.state.selection;
      if (from === to) {
        setPos(null);
        return;
      }
      const domSelection = window.getSelection();
      if (!domSelection || domSelection.rangeCount === 0) {
        setPos(null);
        return;
      }
      const range = domSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (rect.width === 0) {
        setPos(null);
        return;
      }
      setPos({
        top: rect.top - 40,
        left: rect.left + rect.width / 2,
      });
    };

    editor.on('selectionUpdate', updatePos);
    editor.on('blur', () => setPos(null));
    return () => {
      editor.off('selectionUpdate', updatePos);
    };
  }, [editor]);

  if (!editor || !pos) return null;

  const currentColor = editor.getAttributes('textStyle').color as string | undefined;
  const currentSize = editor.getAttributes('textStyle').fontSize as string | undefined;

  return createPortal(
    <div
      ref={portalRef}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: 'translateX(-50%)',
        zIndex: 99999,
      }}
      className="flex flex-col items-center"
    >
      <div className="flex bg-white rounded shadow-lg border border-gray-200 overflow-visible">
        <BubbleButton label="B" isActive={editor.isActive('bold')} onMouseDown={() => editor.chain().focus().toggleBold().run()} />
        <BubbleButton label="I" isActive={editor.isActive('italic')} onMouseDown={() => editor.chain().focus().toggleItalic().run()} />
        <BubbleButton label="U" isActive={editor.isActive('underline')} onMouseDown={() => editor.chain().focus().toggleUnderline().run()} />
        <BubbleButton label="S" isActive={editor.isActive('strike')} onMouseDown={() => editor.chain().focus().toggleStrike().run()} />

        {/* Separator */}
        <div className="w-px bg-gray-200 my-1" />

        {/* Color button */}
        <div className="relative">
          <button
            className={`px-2 py-1 text-xs font-semibold cursor-pointer ${showColors ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}
            onMouseDown={(e) => { e.preventDefault(); setShowColors(!showColors); setShowSizes(false); }}
          >
            <span style={{ color: currentColor || 'currentColor' }}>A</span>
          </button>
          {showColors && (
            <div
              className="absolute top-full left-1/2 -translate-x-1/2 mt-1 rounded-md shadow-lg border border-gray-200 overflow-hidden"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Sketch
                color={currentColor || '#000000'}
                disableAlpha
                presetColors={SKETCH_PRESET_COLORS}
                onChange={(color) => {
                  editor.chain().focus().setColor(color.hex).run();
                }}
                style={{ boxShadow: 'none', border: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Font size button */}
        <div className="relative">
          <button
            className={`px-2 py-1 text-xs font-semibold cursor-pointer ${showSizes ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}
            onMouseDown={(e) => { e.preventDefault(); setShowSizes(!showSizes); setShowColors(false); }}
          >
            {currentSize ? currentSize.replace('px', '') : 'Aa'}
          </button>
          {showSizes && (
            <div
              className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white rounded-md shadow-lg border border-gray-200 p-1 flex flex-col min-w-[52px] w-max"
              onMouseDown={(e) => e.preventDefault()}
            >
              {FONT_SIZES.map(size => (
                <button
                  key={size}
                  className={`px-2 py-0.5 text-xs text-left cursor-pointer rounded border-none whitespace-nowrap ${currentSize === size ? 'bg-blue-100 text-blue-700' : 'bg-transparent text-gray-700 hover:bg-gray-100'}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().setFontSize(size).run();
                    setShowSizes(false);
                  }}
                >
                  {size.replace('px', '')}
                </button>
              ))}
              <button
                className="px-2 py-0.5 text-xs text-left cursor-pointer rounded border-none whitespace-nowrap bg-transparent text-gray-400 hover:bg-gray-100"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().unsetFontSize().run();
                  setShowSizes(false);
                }}
              >
                Reset
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ============================================
// InlineRichEditor: Tiptap-based inline editor
// ============================================

export function InlineRichEditor({ entityId, html, text, onSave, onCancel, className }: InlineRichEditorProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const bubbleInteracting = useRef(false);

  const initialContent = html
    ? html
    : text
      ? text.split('\n').map(line => `<p>${line || '<br>'}</p>`).join('')
      : '<p></p>';

  const editor = useEditor({
    extensions: TIPTAP_EXTENSIONS,
    content: initialContent,
    autofocus: 'end',
  }, [entityId]);

  // Track mousedown/mouseup on the bubble portal to detect non-focusable interactions (canvas, sliders)
  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      if (bubbleRef.current?.contains(e.target as Node)) {
        bubbleInteracting.current = true;
      }
    };
    const onDocUp = () => {
      if (bubbleInteracting.current) {
        bubbleInteracting.current = false;
        editor?.commands.focus();
      }
    };
    document.addEventListener('mousedown', onDocDown, true);
    document.addEventListener('mouseup', onDocUp, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown, true);
      document.removeEventListener('mouseup', onDocUp, true);
    };
  }, [editor]);

  const handleSaveAndStop = useCallback(() => {
    if (!editor) return;
    const editorHtml = editor.getHTML();
    const editorText = editor.getText({ blockSeparator: '\n' });
    onSave(editorHtml, editorText);
  }, [editor, onSave]);

  // Escape handler (capture phase)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onCancel]);

  // Wheel event isolation
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return;
      e.stopPropagation();
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`inline-rich-editor ${className ?? ''}`}
      onPointerDown={e => e.stopPropagation()}
      onBlur={(e) => {
        const related = e.relatedTarget as Node | null;
        if (bubbleInteracting.current) return;
        if (!e.currentTarget.contains(related) && !bubbleRef.current?.contains(related)) {
          handleSaveAndStop();
        }
      }}
    >
      <style>{`
.inline-rich-editor .ProseMirror { padding: 0; margin: 0; height: 100%; outline: none; }
.inline-rich-editor .ProseMirror p { margin: 0; padding: 0; }
.inline-rich-editor .ProseMirror p + p { margin-top: 2px; }
      `}</style>
      <InlineRichBubbleMenu editor={editor} portalRef={bubbleRef} />
      <EditorContent editor={editor} className="w-full h-full" />
    </div>
  );
}
